import { StandardRecordDisciplineSelect } from '../../record-discipline/components/standard-record-discipline-select';
import { CompetitionTypeSelect } from '~/pages/competition/components/competition-type-select';
import { WeightClassSelect } from '~/pages/weight/components/weight-class-select';
import { CategorySelect } from '~/pages/categories/components/category-select';
import { DistrictSelect } from '~/pages/district/components/district-select';
import { useUnsavedChangesGuard } from '~/hooks/useUnsavedChangesGuard';
import { schema, Schema } from './standard-record-form.schema';
import { useAuthentication } from '~/hooks/useAuthentication';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '~/components/Button/Button';
import { Grid } from '~/components/Grid/Grid';
import { Input } from '~/components/Input';
import { Resolver, useForm } from 'react-hook-form';

interface Props {
  onSubmit: (data: Schema) => void;
  defaultValues?: Partial<Schema>;
  isLoading?: boolean;
  mode: 'create' | 'edit';
}

export function StandardRecordForm(props: Props) {
  const { defaultValues, mode, isLoading, onSubmit } = props;
  const { isSuperUser } = useAuthentication();

  const { formState, register, handleSubmit, watch, setValue, getValues } = useForm<Schema>({
    resolver: zodResolver(schema) as unknown as Resolver<Schema>,
    defaultValues: defaultValues ?? {},
  });

  const { dialog } = useUnsavedChangesGuard(formState);

  function handleCompetitionTypeChange() {
    setValue('age_category', undefined!, { shouldDirty: true });
    setValue('weight_class', undefined!, { shouldDirty: true });
  }

  function handleAgeCategoryChange() {
    setValue('weight_class', undefined!, { shouldDirty: true });
  }

  const competitionType = watch('competition_type');

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
      <Grid>
        <Grid.Item cols={{ sm: 12, md: 6 }}>
          <Input.Group formState={formState} htmlFor="district" label="Distrikt">
            <DistrictSelect
              {...register('district', { disabled: !isSuperUser })}
              placeholder={'Svenskt Rekord'}
              formState={formState}
            />
          </Input.Group>
        </Grid.Item>

        <Grid.Item cols={{ sm: 12, md: 6 }}>
          <Input.Group formState={formState} htmlFor="discipline" label="Disciplin">
            <StandardRecordDisciplineSelect
              {...register('discipline')}
              placeholder={'Välj disciplin'}
              formState={formState}
            />
          </Input.Group>
        </Grid.Item>
      </Grid>

      <Grid>
        <Grid.Item cols={{ sm: 12, md: 6 }}>
          <Input.Group formState={formState} htmlFor="competition_type" label="Tävlingstyp">
            <CompetitionTypeSelect
              {...register('competition_type', { onChange: handleCompetitionTypeChange })}
              defaultValue={getValues('competition_type')}
              placeholder={'Välj tävlingstyp'}
              formState={formState}
            />
          </Input.Group>
        </Grid.Item>

        <Grid.Item cols={{ sm: 12, md: 6 }}>
          <Input.Group formState={formState} htmlFor="age_category" label="Ålderskategori">
            <CategorySelect
              {...register('age_category', { onChange: handleAgeCategoryChange })}
              query={{
                competition_types: competitionType ? [competitionType] : undefined,
                has_records: true,
              }}
              defaultValue={getValues('age_category')}
              placeholder={'Välj ålderskategori'}
              formState={formState}
            />
          </Input.Group>
        </Grid.Item>
      </Grid>

      <Grid>
        <Grid.Item cols={{ sm: 12, md: 6 }}>
          <Input.Group formState={formState} htmlFor="weight_class" label="Viktklass">
            <WeightClassSelect
              {...register('weight_class')}
              query={{
                competition_types: competitionType ? [competitionType] : undefined,
                age_category: watch('age_category') ?? undefined,
              }}
              defaultValue={getValues('weight_class')}
              placeholder={'Välj viktklass'}
              formState={formState}
            />
          </Input.Group>
        </Grid.Item>

        <Grid.Item cols={{ sm: 12, md: 6 }}>
          <Input.Group formState={formState} htmlFor="date" label="Datum">
            <Input.Field type="date" {...register('date')} formState={formState} placeholder="Datum" />
          </Input.Group>
        </Grid.Item>
      </Grid>

      <Grid>
        <Grid.Item cols={{ sm: 12, md: 6 }}>
          <Input.Group formState={formState} htmlFor="result" label="Resultat">
            <Input.Field
              type="number"
              step="0.05"
              {...register('result', { valueAsNumber: true })}
              formState={formState}
              placeholder="Sätt resultat"
            />
          </Input.Group>
        </Grid.Item>
      </Grid>

      <Button.Group align="right">
        <Button type="submit" isLoading={isLoading}>
          {{ create: 'Skapa', edit: 'Spara' }[mode]}
        </Button>
      </Button.Group>

      {dialog}
    </form>
  );
}
